/**
 * Drug information screen — full details for a single drug.
 * Collapsible sections with "Speak this" buttons for TTS.
 */

import type { Drug } from '../../types/index';
import { getDrugById } from '../../core/drug-store';
import { speak, stop as stopSpeech } from '../../voice/tts';
import { t } from '../../i18n';

// -----------------------------------------------------------------------
// Pill image lightbox — full-screen zoom on tap
// -----------------------------------------------------------------------

function showPillLightbox(src: string, caption: string): void {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed; inset: 0; z-index: 9000;
    background: rgba(0,0,0,0.85);
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    gap: 16px; padding: 24px;
    animation: pillLightboxIn 0.2s ease;
    cursor: pointer;
  `;

  const img = document.createElement('img');
  img.src = src;
  img.alt = caption;
  img.style.cssText = `
    max-width: 90vw; max-height: 70vh;
    object-fit: contain;
    border-radius: 16px;
    box-shadow: 0 8px 40px rgba(0,0,0,0.4);
    background: #fff;
    padding: 16px;
  `;
  overlay.appendChild(img);

  const label = document.createElement('div');
  label.style.cssText = 'color: #fff; font-size: 18px; font-weight: 600; text-align: center; max-width: 90vw;';
  label.textContent = caption;
  overlay.appendChild(label);

  const hint = document.createElement('div');
  hint.style.cssText = 'color: rgba(255,255,255,0.6); font-size: 14px;';
  hint.textContent = t('drugInfo.tapClose');
  overlay.appendChild(hint);

  // Inject animation
  if (!document.getElementById('pill-lightbox-style')) {
    const style = document.createElement('style');
    style.id = 'pill-lightbox-style';
    style.textContent = `
      @keyframes pillLightboxIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }

  overlay.addEventListener('click', () => {
    overlay.style.opacity = '0';
    overlay.style.transition = 'opacity 0.2s ease';
    setTimeout(() => overlay.remove(), 200);
  });

  document.body.appendChild(overlay);
}

// -----------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------

function createCollapsible(
  title: string,
  content: string | string[],
  drugName: string,
): HTMLElement {
  const section = document.createElement('div');
  section.className = 'flex-col mb-md';

  // Header button
  const header = document.createElement('button');
  header.className = 'collapsible-header';
  header.setAttribute('aria-expanded', 'false');
  header.setAttribute('aria-label', t('drugInfo.expandAriaLabel', { section: title }));

  const headerText = document.createElement('span');
  headerText.textContent = title;
  header.appendChild(headerText);

  const chevron = document.createElement('span');
  chevron.className = 'chevron';
  chevron.setAttribute('aria-hidden', 'true');
  chevron.textContent = '\u25BC'; // down arrow
  header.appendChild(chevron);

  section.appendChild(header);

  // Body
  const body = document.createElement('div');
  body.className = 'collapsible-body';
  body.setAttribute('role', 'region');
  body.setAttribute('aria-label', title + ' details');

  const contentText = Array.isArray(content) ? content.join('. ') : content;

  if (Array.isArray(content) && content.length > 1) {
    const list = document.createElement('ul');
    list.style.cssText = 'padding-left: var(--space-lg); color: var(--color-text-muted); font-size: var(--font-size-body);';
    for (const item of content) {
      const li = document.createElement('li');
      li.textContent = item;
      li.style.marginBottom = 'var(--space-xs)';
      list.appendChild(li);
    }
    body.appendChild(list);
  } else {
    const para = document.createElement('p');
    para.className = 'text-muted';
    para.textContent = contentText || t('drugInfo.noInfo');
    body.appendChild(para);
  }

  // Speak button
  const speakBtn = document.createElement('button');
  speakBtn.className = 'btn btn-outline mt-md';
  speakBtn.style.fontSize = 'var(--font-size-small)';
  speakBtn.setAttribute('aria-label', t('drugInfo.speakAriaLabel', { section: title }));

  const speakIcon = document.createElement('span');
  speakIcon.setAttribute('aria-hidden', 'true');
  speakIcon.textContent = '\uD83D\uDD0A'; // speaker emoji
  speakBtn.appendChild(speakIcon);

  const speakLabel = document.createElement('span');
  speakLabel.textContent = ' ' + t('drugInfo.speakThis').replace(/^[^\s]+\s*/, '');
  speakBtn.appendChild(speakLabel);

  speakBtn.addEventListener('click', (e: Event) => {
    e.stopPropagation();
    const textToSpeak = drugName + '. ' + title + '. ' + contentText;
    stopSpeech();
    speak(textToSpeak);
  });

  body.appendChild(speakBtn);
  section.appendChild(body);

  // Toggle
  header.addEventListener('click', () => {
    const expanded = header.getAttribute('aria-expanded') === 'true';
    header.setAttribute('aria-expanded', String(!expanded));
    header.setAttribute('aria-label',
      expanded
        ? t('drugInfo.expandAriaLabel', { section: title })
        : t('drugInfo.collapseAriaLabel', { section: title }),
    );
    body.classList.toggle('expanded', !expanded);
  });

  return section;
}

// -----------------------------------------------------------------------
// Render
// -----------------------------------------------------------------------

export async function renderDrugInfoScreen(
  container: HTMLElement,
  drugId: string,
): Promise<void> {
  container.textContent = '';
  container.className = 'screen';

  const wrapper = document.createElement('div');
  wrapper.className = 'flex-col gap-md';
  wrapper.style.maxWidth = '600px';
  wrapper.style.margin = '0 auto';

  // Back button
  const backBtn = document.createElement('button');
  backBtn.className = 'btn btn-outline';
  backBtn.style.alignSelf = 'flex-start';
  backBtn.setAttribute('aria-label', t('drugInfo.backAriaLabel'));

  const backArrow = document.createElement('span');
  backArrow.setAttribute('aria-hidden', 'true');
  backArrow.textContent = '\u2190'; // left arrow
  backBtn.appendChild(backArrow);

  const backText = document.createElement('span');
  backText.textContent = ' ' + t('drugInfo.back').replace(/^[^\s]+\s*/, '');
  backBtn.appendChild(backText);

  backBtn.addEventListener('click', () => {
    window.location.hash = '#med-list';
  });
  wrapper.appendChild(backBtn);

  // Load drug
  let drug: Drug | null = null;
  try {
    drug = await getDrugById(drugId);
  } catch {
    // ignore
  }

  if (!drug) {
    const notFound = document.createElement('div');
    notFound.className = 'card text-center';
    notFound.style.padding = 'var(--space-2xl)';
    notFound.textContent = t('drugInfo.notFound');
    wrapper.appendChild(notFound);
    container.appendChild(wrapper);
    return;
  }

  // Drug header
  const headerSection = document.createElement('div');
  headerSection.className = 'card';
  headerSection.setAttribute('role', 'region');
  headerSection.setAttribute('aria-label', t('drugInfo.overviewAriaLabel', { drug: drug.generic_name }));

  // Pill image — use SVG illustrations for known drugs, default for others
  {
    const imgWrapper = document.createElement('div');
    imgWrapper.className = 'text-center mb-md';

    const img = document.createElement('img');
    // Map known drug names to their pill SVGs
    const pillMap: Record<string, string> = {
      'metformin': '/pill-images/metformin-500.svg',
      'lisinopril': '/pill-images/lisinopril-10.svg',
      'atorvastatin': '/pill-images/atorvastatin-20.svg',
      'omeprazole': '/pill-images/omeprazole-20.svg',
    };
    const lowerName = drug.generic_name.toLowerCase();
    let pillSrc = '/pill-images/default-pill.svg';
    for (const [key, path] of Object.entries(pillMap)) {
      if (lowerName.includes(key)) { pillSrc = path; break; }
    }
    img.src = drug.pill_image_path || pillSrc;
    img.alt = drug.generic_name + ' pill' +
      (drug.pill_color ? ': ' + drug.pill_color + ' ' + drug.pill_shape : '');
    img.style.cssText = 'width: 180px; height: 180px; object-fit: contain; border-radius: var(--radius-md); cursor: pointer; transition: transform 0.15s ease;';
    img.onerror = () => {
      img.onerror = null;
      img.src = '/pill-images/default-pill.svg';
    };

    // Tap to zoom — full-screen lightbox
    img.addEventListener('click', () => {
      showPillLightbox(img.src, drug.generic_name +
        (drug.pill_color ? ' — ' + drug.pill_color + ' ' + drug.pill_shape : ''));
    });

    const tapHint = document.createElement('div');
    tapHint.textContent = t('drugInfo.tapEnlarge');
    tapHint.style.cssText = 'font-size: 11px; color: var(--color-text-dim); text-align: center; margin-top: 4px;';
    imgWrapper.appendChild(img);
    imgWrapper.appendChild(tapHint);
    headerSection.appendChild(imgWrapper);
  }

  // Name
  const nameEl = document.createElement('h2');
  nameEl.className = 'text-center';
  nameEl.textContent = drug.generic_name;
  headerSection.appendChild(nameEl);

  // Brand names
  if (drug.brand_names.length > 0) {
    const brandEl = document.createElement('p');
    brandEl.className = 'text-center text-muted';
    brandEl.textContent = t('drugInfo.alsoKnownAs', { brands: drug.brand_names.join(', ') });
    headerSection.appendChild(brandEl);
  }

  // Speak all button
  const speakAllBtn = document.createElement('button');
  speakAllBtn.className = 'btn btn-primary btn-block mt-md';
  speakAllBtn.setAttribute('aria-label', t('drugInfo.readAllAriaLabel'));

  const speakAllIcon = document.createElement('span');
  speakAllIcon.setAttribute('aria-hidden', 'true');
  speakAllIcon.textContent = '\uD83D\uDD0A ';
  speakAllBtn.appendChild(speakAllIcon);

  const speakAllLabel = document.createElement('span');
  speakAllLabel.textContent = t('drugInfo.readAll').replace(/^[^\s]+\s*/, '');
  speakAllBtn.appendChild(speakAllLabel);

  speakAllBtn.addEventListener('click', () => {
    stopSpeech();
    const parts: string[] = [
      drug!.generic_name + '.',
      drug!.description || '',
      t('drugInfo.tts.howToTake') + (drug!.dosage_instructions || t('drugInfo.tts.noInstructions')),
      drug!.side_effects.length > 0 ? t('drugInfo.tts.sideEffects') + drug!.side_effects.join(', ') + '.' : '',
      drug!.warnings.length > 0 ? t('drugInfo.tts.warnings') + drug!.warnings.join('. ') : '',
      drug!.food_interactions.length > 0 ? t('drugInfo.tts.foodInteractions') + drug!.food_interactions.join(', ') : '',
      drug!.storage ? t('drugInfo.tts.storage') + drug!.storage : '',
      drug!.missed_dose ? t('drugInfo.tts.missedDose') + drug!.missed_dose : '',
    ];
    speak(parts.filter(Boolean).join(' '));
  });
  headerSection.appendChild(speakAllBtn);

  wrapper.appendChild(headerSection);

  // Collapsible sections
  const sections: [string, string | string[]][] = [
    [t('drugInfo.description'), drug.description],
    [t('drugInfo.howToTake'), drug.dosage_instructions],
    [t('drugInfo.sideEffects'), drug.side_effects],
    [t('drugInfo.warnings'), drug.warnings],
    [t('drugInfo.foodInteractions'), drug.food_interactions],
    [t('drugInfo.storage'), drug.storage],
    [t('drugInfo.missedDose'), drug.missed_dose],
  ];

  for (const [sectionTitle, sectionContent] of sections) {
    // Skip empty sections
    const hasContent = Array.isArray(sectionContent)
      ? sectionContent.length > 0
      : Boolean(sectionContent);

    if (hasContent) {
      wrapper.appendChild(
        createCollapsible(sectionTitle, sectionContent, drug.generic_name),
      );
    }
  }

  container.appendChild(wrapper);
}
